import React, { useState, useEffect, useRef } from 'react';
import SectionCards from './SectionCards';
import SplitDown from './SplitDown';
import SubtaskPlacement, { arrowOffers, handedArrows } from './SubtaskPlacement';
import { getTask, updateTask, deleteTask, getTasks, addPredecessor, removePredecessor, updatePredecessor, previewSchedule, getTaskWorkProgress, getTaskDocuments, getSplitPreview } from '../api';
import ConfirmDialog from './ConfirmDialog';
import TaskSOVLinks from './TaskSOVLinks';
import useConfirm from '../hooks/useConfirm';
import ResponsiblePartyField from './subs/ResponsiblePartyField';
import DateRangePicker, { workSpan } from './DateRangePicker';
import { PrimaryButton } from './subs/SubUi';
import { whoOf } from './subs/LineWork';
import { fmtRange } from './subs/SubMovesBanner';
import { basisOf, dayOf, finishForPct, maxPctOf } from './subs/SubProgressBanner';
import MilestoneColorPicker from './MilestoneColorPicker';

/* ──────────────────────────────────────────────────────────────────
 * TaskEditModal — "Task Properties" popup
 *
 * Warm-cream + sage token redesign from the Claude Design handoff
 * `~/Downloads/design_handoff_task_properties` (2026-05-27).
 *
 * Behavior preserved verbatim from the prior slate-palette version:
 *  - Loads getTask + getTasks (skipped if availableTasks is passed in
 *    from ScheduleView's shared state).
 *  - Status ↔ Completion bidirectional coupling (drag slider/type a
 *    pct → status flips, and changing status flips pct to 0/50/100).
 *  - Milestone ↔ Task Type coupling (toggling Milestone sets Type to
 *    Milestone, and vice versa).
 *  - Predecessor add/edit/remove with inline picker that stays open
 *    after a successful add so the user can chain.
 *  - Dependent Tasks read-only roll-up (computed from allTasks).
 *  - SOV Links section delegated to <TaskSOVLinks/> below.
 *  - 2-step delete via shared ConfirmDialog.
 *  - Body scroll lock while open (prev value restored on unmount).
 *  - Double-submit guards on save/delete/predecessor add.
 *  - Save error surfaces as an inline danger banner above the footer.
 * ────────────────────────────────────────────────────────────────── */

const DEP_TYPES = [
  { value: 'FS', label: 'FS — Finish to Start', short: 'FS' },
  { value: 'SS', label: 'SS — Start to Start', short: 'SS' },
  { value: 'FF', label: 'FF — Finish to Finish', short: 'FF' },
  { value: 'SF', label: 'SF — Start to Finish', short: 'SF' },
];

// A link's lag counts work days, or calendar days that keep going over the weekend
// (Project's "ed", e.g. a concrete cure — #55).
const LAG_UNITS = [
  { value: 'd', label: 'work days' },
  { value: 'ed', label: 'calendar days' },
];
const LAG_UNIT_TITLE = 'Work days skip weekends; calendar days keep counting through them (like MS Project\'s "ed")';

// Documents (2026-10-24): a task whose work is the sub's documents -- O&M manuals, as-builts, training records,
// submittals -- uploaded on E3 Subs.
const TASK_TYPES = ['Labor', 'Material', 'Procurement', 'Engineering', 'Inspection', 'Documents', 'Milestone'];

const TYPE_COLOR = {
  Labor:       'var(--type-labor)',
  Material:    'var(--type-material)',
  Procurement: 'var(--type-procurement)',
  Engineering: 'var(--type-engineering)',
  Inspection:  'var(--type-inspection)',
  Documents:   'var(--type-documents)',
  Milestone:   'var(--type-milestone)',
};

const STATUS_META = {
  'Not Started': { color: 'var(--text-3)',      bg: 'var(--bg-surface-2)' },
  'In Progress': { color: 'var(--accent-text)', bg: 'var(--accent-soft)' },
  'Complete':    { color: 'var(--success)',     bg: 'var(--success-soft)' },
};

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const statusFromPct = (p) => {
  const n = Number(p) || 0;
  if (n >= 100) return 'Complete';
  if (n > 0) return 'In Progress';
  return 'Not Started';
};

/* ─────────────────────── Tiny stateless primitives ─────────────────────── */

const Pill = ({ color, bg, dot, children }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '2px 8px', borderRadius: 999,
    fontSize: 12.5, fontWeight: 500, color, background: bg,
    border: `1px solid color-mix(in oklch, ${color} 22%, transparent)`,
    letterSpacing: '-0.005em', whiteSpace: 'nowrap',
  }}>
    {dot && <span style={{ width: 5, height: 5, borderRadius: 99, background: color }} />}
    {children}
  </span>
);

// A section's header, read by SectionCards as its card's title (and `right`); not drawn by itself (Duncan 2026-10-30).
const SectionHeader = () => null;
SectionHeader.isSectionHead = true;
// eslint-disable-next-line no-unused-vars
const SectionHeaderLine = ({ title, right, first = false }) => (
  <div style={{
    display: 'flex', alignItems: 'center',
    padding: first ? '4px 0 10px' : '22px 0 10px',
    borderBottom: '1px solid var(--border-subtle)',
    marginBottom: 12,
  }}>
    <h3 style={{
      margin: 0, fontSize: 12.5, fontWeight: 600,
      textTransform: 'uppercase', letterSpacing: '0.08em',
      color: 'var(--text-3)',
    }}>{title}</h3>
    <div style={{ flex: 1 }} />
    {right}
  </div>
);

const Label = ({ children }) => (
  <label style={{
    display: 'block', fontSize: 12, fontWeight: 500,
    color: 'var(--text-3)', textTransform: 'uppercase',
    letterSpacing: '0.05em', marginBottom: 6,
  }}>{children}</label>
);

const inputBaseStyle = {
  width: '100%', height: 34, padding: '0 10px',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)', borderRadius: 8,
  fontSize: 14.5, color: 'var(--text-1)',
  letterSpacing: '-0.005em',
  outline: 'none', boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const onFocusRing = (e) => {
  e.currentTarget.style.borderColor = 'var(--accent)';
  e.currentTarget.style.boxShadow = '0 0 0 2px var(--accent-ring)';
};
const onBlurRing = (e) => {
  e.currentTarget.style.borderColor = 'var(--border-default)';
  e.currentTarget.style.boxShadow = 'none';
};

/* ─────────────────────── Inline-SVG icons (no extra deps) ─────────────────────── */

const XIcon = ({ size = 16, sw = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
const TrashIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
  </svg>
);
const PlusIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const SearchIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
  </svg>
);
const ArrowRightIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h13M13 6l6 6-6 6" />
  </svg>
);
const CalendarIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, color: 'var(--text-3)' }}>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4" />
  </svg>
);
const LockIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const AlertCircleIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" />
  </svg>
);
const CheckIcon = ({ size = 11, sw = 2.4 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const Spinner = ({ size = 13, color = '#fff' }) => (
  <span style={{
    display: 'inline-block', width: size, height: size, borderRadius: 99,
    border: `2px solid color-mix(in oklch, ${color} 30%, transparent)`,
    borderTopColor: color,
    animation: 'spin .8s linear infinite',
    flexShrink: 0,
  }} />
);

/* ─────────────────────── Checkbox ─────────────────────── */

const Check = ({ checked, onChange, label, disabled }) => (
  <label style={{
    display: 'inline-flex', alignItems: 'center', gap: 8, position: 'relative',
    cursor: disabled ? 'not-allowed' : 'pointer', userSelect: 'none',
    opacity: disabled ? 0.6 : 1,
  }}>
    <input
      type="checkbox"
      checked={!!checked}
      onChange={onChange}
      disabled={disabled}
      style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
    />
    <span style={{
      width: 16, height: 16, borderRadius: 4,
      background: checked ? 'var(--accent)' : 'var(--bg-surface)',
      border: `1px solid ${checked ? 'var(--accent)' : 'var(--border-default)'}`,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', flexShrink: 0, transition: 'background .12s, border-color .12s',
    }}>
      {checked && <CheckIcon />}
    </span>
    <span style={{ fontSize: 14.5, color: 'var(--text-1)' }}>{label}</span>
  </label>
);

/* ─────────────────────── Completion slider ─────────────────────── */

/** A suggestion in the sub progress note (2026-10-28): a day from the sub's own work on E3 Subs, and a button that
    fills it in above. */
const SubHint = ({ title, text, button, onClick, disabled }) => (
  <div style={{
    marginTop: 10, display: 'flex', alignItems: 'center', gap: 12, padding: '9px 11px', borderRadius: 8,
    background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
  }}>
    <span style={{ color: 'var(--warning)', display: 'inline-flex', flex: '0 0 auto' }}><CalendarIcon size={16} /></span>
    <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.45, color: 'var(--text-1)' }}>
      <b style={{ fontWeight: 600 }}>{title}</b> {text}
    </div>
    <PrimaryButton disabled={disabled} onClick={onClick} style={{ height: 30, fontSize: 13, padding: '0 12px', flex: '0 0 auto' }}>{button}</PrimaryButton>
  </div>
);

/** A bar in the sub progress note (2026-10-28): what the schedule allows by the billing date (a marker where it stops),
    or, striped amber, what the sub says. */
const SubLane = ({ label, pct, marker = false, sub = false }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '118px minmax(0, 1fr) 44px', gap: 8, alignItems: 'center', marginTop: 8, fontSize: 12.5 }}>
    <span style={{ color: 'var(--text-3)' }}>{label}</span>
    <div style={{ position: 'relative', height: 10, borderRadius: 5, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, borderRadius: 5,
        background: sub ? 'repeating-linear-gradient(45deg, var(--warning) 0 4px, color-mix(in oklch, var(--warning) 45%, transparent) 4px 8px)' : 'var(--success)',
      }} />
      {marker && <div style={{ position: 'absolute', left: `calc(${pct}% - 1px)`, top: -4, bottom: -4, width: 2, borderRadius: 1, background: 'var(--text-1)' }} />}
    </div>
    <span className="mono" style={{ textAlign: 'right', color: 'var(--text-1)' }}>{pct}%</span>
  </div>
);

// `mark`: where a sub says the task is, on E3 Subs (2026-10-28) -- an amber tick on the track.
// `floor`: the lowest % it can go without billing a line backwards (server billingFloor.js) -- marked on the track.
const CompletionSlider = ({ value, onChange, disabled, mark = null, floor = null }) => {
  const [dragging, setDragging] = useState(false);
  const done = value === 100;
  const empty = value === 0;
  const trackColor = done ? 'var(--success)' : empty ? 'var(--border-default)' : 'var(--accent)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
      <div style={{ flex: 1, position: 'relative', height: 28, display: 'flex', alignItems: 'center' }}>
        <div style={{ position: 'relative', width: '100%', height: 6, background: 'var(--border-subtle)', borderRadius: 99 }}>
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0, width: `${value}%`,
            background: trackColor, borderRadius: 99,
            opacity: empty ? 0 : 1,
            transition: dragging ? 'none' : 'width .15s, background .12s',
          }} />
          {mark != null && (
            <div style={{
              position: 'absolute', left: `calc(${mark}% - 1px)`, top: -5, bottom: -5, width: 2, borderRadius: 1,
              background: 'var(--warning)', pointerEvents: 'none',
            }} />
          )}
          {floor != null && floor > 0 && (
            <div style={{
              position: 'absolute', left: `calc(${floor}% - 1px)`, top: -7, bottom: -7, width: 2, borderRadius: 1,
              background: 'var(--warning)', pointerEvents: 'none',
            }} />
          )}
          <div style={{
            position: 'absolute', left: `calc(${value}% - 9px)`, top: '50%',
            transform: dragging ? 'translateY(-50%) scale(1.15)' : 'translateY(-50%)',
            width: 18, height: 18, borderRadius: 99,
            background: '#fff', border: `2px solid ${empty ? 'var(--border-strong)' : trackColor}`,
            boxShadow: dragging
              ? `0 0 0 6px color-mix(in oklch, ${trackColor} 18%, transparent), 0 2px 4px oklch(0.3 0.02 90 / 0.15)`
              : '0 1px 2px oklch(0.3 0.02 90 / 0.15)',
            transition: dragging ? 'none' : 'box-shadow .12s, transform .12s, border-color .12s',
            pointerEvents: 'none',
          }} />
        </div>
        <input
          type="range" min={0} max={100} step={1}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(Math.max(floor || 0, parseInt(e.target.value, 10) || 0))}
          onMouseDown={() => setDragging(true)}
          onMouseUp={() => setDragging(false)}
          onTouchStart={() => setDragging(true)}
          onTouchEnd={() => setDragging(false)}
          onBlur={() => setDragging(false)}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            opacity: 0, cursor: disabled ? 'not-allowed' : 'pointer', margin: 0,
          }}
        />
      </div>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 2,
        height: 32, padding: '0 8px 0 10px',
        background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 7,
        fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em',
        fontSize: 14.5, color: empty ? 'var(--text-4)' : 'var(--text-1)', fontWeight: 500,
        minWidth: 70,
      }}>
        <input
          type="number" min={0} max={100} step={1}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(Math.min(100, Math.max(0, parseInt(e.target.value, 10) || 0)))}
          // typed freely (30 starts as 3), then held at the floor once the box is left
          onBlur={() => { if (floor && value < floor) onChange(floor); }}
          style={{
            flex: 1, width: '100%', minWidth: 0,
            background: 'transparent', border: 'none', outline: 'none',
            textAlign: 'right',
            fontFamily: 'inherit', letterSpacing: 'inherit', fontSize: 'inherit', fontWeight: 'inherit',
            color: 'inherit', padding: 0,
          }}
        />
        <span style={{ color: 'var(--text-3)', fontSize: 13.5 }}>%</span>
      </div>
    </div>
  );
};

const EmptyRow = ({ text }) => (
  <div style={{
    padding: '14px 12px',
    background: 'var(--bg-surface-2)',
    border: '1px dashed var(--border-default)',
    borderRadius: 8,
    fontSize: 14, color: 'var(--text-4)',
    marginBottom: 4,
  }}>{text}</div>
);

const ChipBtn = ({ onClick, disabled, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '5px 10px 5px 8px', borderRadius: 7,
      fontSize: 13.5, fontWeight: 500,
      color: 'var(--accent-text)',
      background: 'var(--accent-soft)',
      border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
    }}
  >
    <PlusIcon />{children}
  </button>
);

// A link's lag, saved on Enter or when you leave the box. Each save moves dates, so not on
// every keystroke: typing "12" would save 1 first, and "-1" would save 0.
const LagInput = ({ value, onCommit, style }) => {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  const commit = () => {
    const n = parseInt(draft, 10) || 0;
    if (n !== value) onCommit(n);
    else setDraft(String(value));
  };
  return (
    <input
      type="number"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
      style={style}
      title="Lag days"
    />
  );
};

/* ─────────────────────── Main component ─────────────────────── */

const TaskEditModal = ({ taskId, projectId, onClose, onSaved, onDeleted, onAllocationsChanged, onAddSubtask, availableTasks, workWeek, subProgress = null, focus = null }) => {
  const { confirm, confirmDialogProps } = useConfirm();
  const [task, setTask] = useState(null);
  const [_localAllTasks, _setLocalAllTasks] = useState([]);
  const allTasks = availableTasks && availableTasks.length ? availableTasks : _localAllTasks;
  const setAllTasks = _setLocalAllTasks;
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [arrowPicks, setArrowPicks] = useState({}); // the placement box's arrow check (Move the arrow?), ticked unless unticked
  // The split window (SplitDown.js) over this one: a parent that still holds SOV dollars its subtasks should -- { ids, after }.
  const [lowWarn, setLowWarn] = useState(null);
  const warnedOpen = useRef(false);
  useEffect(() => { // opening a parent that still holds dollars its subtasks should: the split window, on top
    if (!task || warnedOpen.current) return;
    const kids = allTasks.filter((x) => Number(x.parent_task_id) === Number(taskId)).length || Number(task.subtask_count) || 0;
    const mine = allTasks.find((x) => Number(x.id) === Number(taskId)) || {};
    if (!kids || !((task.sov_allocations && task.sov_allocations.length) || mine.split_pending)) return;
    warnedOpen.current = true;
    setLowWarn({ ids: [taskId], after: null });
  }, [task]); // eslint-disable-line
  const [showAddPred, setShowAddPred] = useState(false);
  const [predSearch, setPredSearch] = useState('');
  const [addPredType, setAddPredType] = useState('FS');
  const [addPredLag, setAddPredLag] = useState(0);
  const [addPredElapsed, setAddPredElapsed] = useState(false);
  const [addingPred, setAddingPred] = useState(false);
  // Why the last link change was refused (a loop, a duplicate…), shown in the section.
  const [predError, setPredError] = useState(null);
  const predSearchRef = useRef(null);
  // Microsoft Project-style scheduling (#50–#52). The start comes from the predecessors;
  // typing one pins it ("start no earlier than"); the duration is typed in the project's
  // days and the finish is calculated. schedDirty = a date, the duration, the milestone flag
  // or the pin changed here, so Save sends them and the server recalculates everything after
  // this task; otherwise Save leaves the schedule alone. preview = the server's answer.
  const [schedDirty, setSchedDirty] = useState(false);
  const [preview, setPreview] = useState(null);
  // A start typed here; otherwise the preview works from the saved task, which links change.
  const [startTyped, setStartTyped] = useState(false);
  // Start and Finish on one calendar (Duncan 2026-10-25, like a hotel booking): which field opened it.
  const [picker, setPicker] = useState(null); // 'start' | 'finish' | null
  const startRef = useRef(null);
  const finishRef = useRef(null);
  // Opened from the sub progress banner (`focus` 'progress'; Duncan 2026-10-28: "If you're leading me into something, you
  // need to lead me to exactly where that is"): the dates and the % bar in view, the date to move outlined.
  const scheduleRef = useRef(null);
  const ledRef = useRef(false);
  useEffect(() => {
    if (loading || focus !== 'progress' || ledRef.current || !scheduleRef.current) return;
    ledRef.current = true;
    scheduleRef.current.scrollIntoView({ block: 'start' });
  }, [loading, focus]);
  // When the sub's work started and finished, from E3 Subs (Duncan 2026-10-28: "can we pull the start date from this
  // particular task like the first day that the subcontractor marked something is done there"; and the last, for the
  // finish): the first and last day it marked a line done -- on a Documents task, the day its first document came in.
  const [subWork, setSubWork] = useState(null); // { docs, first, firstBy, last, lastBy } ('YYYY-MM-DD' days here)
  const subKey = subProgress ? subProgress.key : null;
  useEffect(() => {
    if (!subKey || !taskId) return undefined;
    let live = true;
    const docs = !!(subProgress.basis && subProgress.basis.documents);
    const dayHere = (iso) => {
      const d = iso ? new Date(iso) : null;
      return d && Number.isFinite(d.getTime())
        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : null;
    };
    const read = docs
      ? getTaskDocuments(taskId).then((d) => ((d && d.documents) || []).filter((x) => !x.removedAt && x.status !== 'REMOVED').map((x) => ({ at: x.addedAt, by: x.addedBy })))
      : getTaskWorkProgress(taskId).then((p) => ((p && p.lines) || []).filter((l) => l.done && l.doneAt).map((l) => ({ at: l.doneAt, by: l.doneBy })));
    read.then((events) => {
      const dated = events.map((e) => ({ ...e, day: dayHere(e.at) })).filter((e) => e.day).sort((a, b) => String(a.at).localeCompare(String(b.at)));
      const first = dated[0];
      const last = dated[dated.length - 1];
      if (live) setSubWork(first ? { docs, first: first.day, firstBy: whoOf(first.by), last: last.day, lastBy: whoOf(last.by) } : null);
    }).catch(() => {});
    return () => { live = false; };
    // eslint-disable-next-line
  }, [taskId, subKey]);
  // The type a task had before Milestone was ticked, so unticking puts it back.
  const typeBeforeMilestone = useRef(null);

  // Lock body scroll while open; restore on unmount.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Esc closes the modal (unless saving/deleting).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving && !deleting) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving, deleting]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [taskData, tasksData] = await Promise.all([
          getTask(taskId),
          availableTasks && availableTasks.length ? Promise.resolve(null) : getTasks(projectId),
        ]);
        setTask(taskData);
        if (tasksData) setAllTasks(tasksData);
        setFormData({
          task_name: taskData.task_name || '',
          task_description: taskData.task_description || '',
          building_area_phase: taskData.building_area_phase || '',
          task_type: taskData.task_type || 'Engineering',
          start_date: taskData.start_date || '',
          finish_date: taskData.finish_date || '',
          duration_days: taskData.duration_days || 0,
          percent_complete: taskData.percent_complete || 0,
          status: taskData.status || 'Not Started',
          milestone_flag: taskData.milestone_flag || 0,
          milestone_color: taskData.milestone_color || null,
          responsible_party: taskData.responsible_party || '',
          subcontractor_id: taskData.subcontractor_id || null,
          parent_task_id: taskData.parent_task_id || null, // Subtask of
          notes: taskData.notes || '',
          client_visible: taskData.client_visible ?? 1,
          task_code: taskData.task_code || '',
          duration: '',
          constraint_type: taskData.constraint_type || null,
          constraint_date: taskData.constraint_date || null,
        });
      } catch (err) {
        console.error('Error loading task:', err);
      } finally {
        setLoading(false);
      }
    };
    if (taskId) load();
    // eslint-disable-next-line
  }, [taskId, projectId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'milestone_flag' && checked && formData.task_type !== 'Milestone') typeBeforeMilestone.current = formData.task_type;
    setFormData(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value };
      if (name === 'milestone_flag' || (name === 'task_type' && (value === 'Milestone') !== !!prev.milestone_flag)) setSchedDirty(true);
      // Milestone ↔ Task Type coupling
      if (name === 'milestone_flag') {
        if (checked) next.task_type = 'Milestone';
        // Unticked: back to the type it had (a task saved as a milestone had none: Engineering).
        else if (prev.task_type === 'Milestone') next.task_type = typeBeforeMilestone.current || 'Engineering';
      }
      if (name === 'task_type') {
        next.milestone_flag = value === 'Milestone' ? 1 : 0;
      }
      return next;
    });
  };

  // A task carrying billed work stops at what was billed (billing_floor, server billingFloor.js).
  const floorPct = (task && task.billing_floor && Number(task.billing_floor.pct)) || 0;
  const setCompletion = (val) => {
    const pct = Math.min(100, Math.max(0, Number(val) || 0));
    setFormData(prev => ({ ...prev, percent_complete: pct, status: statusFromPct(pct) }));
  };
  const handleStatusChange = (e) => {
    const status = e.target.value;
    let pct = formData.percent_complete;
    if (status === 'Complete' && pct < 100) pct = 100;
    else if (status === 'Not Started' && pct > 0) pct = 0;
    else if (status === 'In Progress' && (pct === 0 || pct === 100)) pct = 50;
    if (pct < floorPct) { setCompletion(floorPct); return; } // billed work can't go back to Not Started (the slider's floor)
    setFormData(prev => ({ ...prev, status, percent_complete: pct }));
  };

  // Ask the server where this task lands — the same engine Save uses — whenever a scheduling
  // input changes (a beat after typing). The first answer also gives the duration in the
  // project's days (working days on a 5-day week) for the Duration box.
  const linkKey = (task?.predecessors || []).map((p) => `${p.predecessor_task_id}:${p.dependency_type}:${p.lag_days}:${p.lag_elapsed ? 'ed' : 'd'}`).join(',');
  useEffect(() => {
    if (!task || !projectId) return undefined;
    let live = true;
    const timer = setTimeout(async () => {
      const pinned = formData.constraint_type === 'SNET' && formData.constraint_date;
      const days = parseInt(formData.duration, 10);
      try {
        const p = await previewSchedule(projectId, {
          task: {
            id: taskId,
            ...(startTyped ? { start_date: formData.start_date } : {}),
            milestone_flag: formData.milestone_flag ? 1 : 0,
            constraint_type: pinned ? 'SNET' : null,
            constraint_date: pinned ? formData.constraint_date : null,
            ...(schedDirty && days > 0 ? { duration_days: days } : {}),
          },
          predecessors: (task.predecessors || []).map((l) => ({ predecessor_task_id: l.predecessor_task_id, dependency_type: l.dependency_type, lag_days: l.lag_days, lag_elapsed: l.lag_elapsed === true })),
        });
        if (!live) return;
        setPreview(p);
        if (!schedDirty && p && p.duration_days) {
          setFormData((prev) => (String(prev.duration) === String(p.duration_days) ? prev : { ...prev, duration: p.duration_days }));
        }
      } catch (err) {
        if (live) setPreview(null);
      }
    }, schedDirty ? 250 : 0);
    return () => { live = false; clearTimeout(timer); };
    // eslint-disable-next-line
  }, [task, linkKey, startTyped, formData.start_date, formData.duration, formData.milestone_flag, formData.constraint_type, formData.constraint_date, schedDirty]);

  const linked = (task?.predecessors || []).length > 0;
  // As in Microsoft Project, a typed start is a pin ("start no earlier than"): it can hold the
  // task later than its links require, never earlier, and it still holds if links are added
  // later. Clearing the box goes back to the saved start.
  const handleStartTyped = (e) => {
    const v = e.target.value;
    if (!v) {
      setStartTyped(false);
      setFormData((prev) => ({ ...prev, start_date: task?.start_date || '' }));
      return;
    }
    setSchedDirty(true);
    setStartTyped(true);
    setFormData((prev) => ({ ...prev, start_date: v, constraint_type: 'SNET', constraint_date: v }));
  };
  const handleDurationChange = (e) => {
    setSchedDirty(true);
    setFormData((prev) => ({ ...prev, duration: e.target.value.replace(/[^0-9]/g, '') }));
  };
  // A finish picked on the calendar sets the duration: the days from the start to it, as Summit counts them.
  const handleFinishPicked = (iso, days) => {
    setSchedDirty(true);
    setFormData((prev) => ({ ...prev, duration: String(Math.max(1, days)) }));
  };
  const removePin = () => {
    setSchedDirty(true);
    setFormData((prev) => ({ ...prev, constraint_type: null, constraint_date: null }));
  };
  // Links are saved at once and move dates on the server; refresh the list and Gantt behind.
  const refreshSchedule = () => { if (onSaved) onSaved(); };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const saveData = {
        task_name: formData.task_name,
        task_description: formData.task_description,
        building_area_phase: formData.building_area_phase,
        task_type: formData.task_type,
        percent_complete: parseFloat(formData.percent_complete) || 0,
        status: formData.status,
        milestone_flag: formData.milestone_flag ? 1 : 0,
        milestone_color: formData.milestone_color || null,
        responsible_party: formData.responsible_party,
        subcontractor_id: formData.subcontractor_id || null,
        parent_task_id: formData.parent_task_id ? Number(formData.parent_task_id) : null, // Subtask of (None takes it back out)
        notes: formData.notes,
        client_visible: formData.client_visible ? 1 : 0,
      };
      if (kidCount > 0) {
        // A parent (server/subtasks.js): its %, status and milestone are its subtasks'; a sub picked goes to all of them.
        delete saveData.percent_complete;
        delete saveData.status;
        delete saveData.milestone_flag;
        delete saveData.milestone_color;
        delete saveData.parent_task_id;
        if (!saveData.subcontractor_id) { delete saveData.subcontractor_id; delete saveData.responsible_party; }
      }
      if (schedDirty) {
        const days = parseInt(formData.duration, 10);
        Object.assign(saveData, {
          start_date: (preview && preview.start_date) || formData.start_date,
          finish_date: (preview && preview.finish_date) || formData.finish_date,
          duration_days: formData.milestone_flag ? 1 : days > 0 ? days : (preview && preview.duration_days) || 1,
          duration_unit: 'project',
          constraint_type: formData.constraint_type === 'SNET' && formData.constraint_date ? 'SNET' : null,
          constraint_date: formData.constraint_type === 'SNET' ? formData.constraint_date : null,
        });
      }
      await updateTask(taskId, saveData);
      // The arrows decision (Duncan 2026-10-30): this subtask now finishes after the one a task outside waits on -- ticked,
      // that arrow moves to it, so that task still waits for the whole of its parent.
      if (saveData.parent_task_id) {
        const ap = allTasks.find((x) => x.id === saveData.parent_task_id);
        const me = { id: Number(taskId), task_name: saveData.task_name, start_date: saveData.start_date || formData.start_date, finish_date: saveData.finish_date || formData.finish_date };
        const offers = ap ? arrowOffers({ parent: ap, siblings: allTasks.filter((x) => Number(x.parent_task_id) === ap.id), me, allTasks, isNew: false }) : [];
        for (const o of offers.filter((x) => x.kind === 'move' && arrowPicks[x.key] !== false)) {
          try {
            await removePredecessor(o.taskId, o.link.id);
            await addPredecessor(o.taskId, { predecessor_task_id: Number(taskId), dependency_type: o.link.dependency_type || 'FS', lag_days: o.link.lag_days || 0, lag_elapsed: o.link.lag_elapsed === true });
          } catch (e2) { console.error('Moving the arrow failed:', e2); }
        }
      }
      // Made a subtask of a task that holds SOV dollars or a sub's billing: the split window, on top of this one, which
      // stays open behind it until it's dealt with.
      if (saveData.parent_task_id && saveData.parent_task_id !== (task && task.parent_task_id)) {
        const held = await getSplitPreview(saveData.parent_task_id).catch(() => null);
        if (held && (held.ours.length || held.subs.length)) {
          if (onSaved) onSaved();
          setLowWarn({ ids: [saveData.parent_task_id], after: () => onClose() });
          return;
        }
      }
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error('Error saving task:', err);
      setSaveError(err.message || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  // "+ Add a subtask" (2026-11-02): leaves this window for Add Task -- after a check when something here isn't saved yet.
  const addSubtaskHere = async () => {
    const same = (a, b) => String(a ?? '') === String(b ?? '');
    const edited = schedDirty || !task
      || ['task_name', 'task_description', 'building_area_phase', 'task_type', 'status', 'responsible_party', 'notes', 'start_date', 'finish_date']
        .some((k) => !same(formData[k], task[k]))
      || Number(formData.percent_complete || 0) !== Number(task.percent_complete || 0)
      || !!Number(formData.client_visible) !== !!Number(task.client_visible)
      || (formData.milestone_color || null) !== (task.milestone_color || null);
    if (edited) {
      const ok = await confirm({
        title: 'Add a subtask',
        message: `Your changes to "${formData.task_name || 'this task'}" aren't saved. Leave them and add a subtask?`,
        confirmLabel: 'Leave and add',
      });
      if (!ok) return;
    }
    onAddSubtask(taskId);
  };

  const handleDelete = async () => {
    if (deleting) return;
    const ok = await confirm({
      title: 'Delete Task',
      message: `Are you sure you want to delete "${formData.task_name}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      setDeleting(true);
      await deleteTask(taskId);
      if (onDeleted) onDeleted();
      onClose();
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Failed to delete task: ' + (err?.message || 'Unknown error'));
    } finally {
      setDeleting(false);
    }
  };

  const handlePredUpdate = async (predId, data) => {
    try {
      await updatePredecessor(taskId, predId, data);
      const updated = await getTask(taskId);
      setTask(updated);
      setPredError(null);
      refreshSchedule();
    } catch (err) {
      console.error('Error updating predecessor:', err);
      setPredError(err.message || "Couldn't change that link.");
    }
  };

  const handleAddPredecessor = async (predecessorTaskId) => {
    if (addingPred) return;
    setAddingPred(true);
    try {
      await addPredecessor(taskId, {
        predecessor_task_id: predecessorTaskId,
        dependency_type: addPredType,
        lag_days: addPredLag,
        lag_elapsed: addPredElapsed,
      });
      const updated = await getTask(taskId);
      setTask(updated);
      setPredError(null);
      refreshSchedule();
      setPredSearch('');
      setAddPredLag(0);
      setAddPredElapsed(false);
      setTimeout(() => predSearchRef.current?.focus(), 30);
    } catch (err) {
      console.error('Error adding predecessor:', err);
      setPredError(err.message || "Couldn't add that predecessor.");
    } finally {
      setAddingPred(false);
    }
  };

  const handlePredRemove = async (predId) => {
    const ok = await confirm({
      title: 'Remove Predecessor',
      message: 'Remove this predecessor link?',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await removePredecessor(taskId, predId);
      const updated = await getTask(taskId);
      setTask(updated);
      setPredError(null);
      refreshSchedule();
    } catch (err) {
      console.error('Error removing predecessor:', err);
      setPredError(err.message || "Couldn't remove that link.");
    }
  };

  const pct = formData.percent_complete || 0;
  // A parent (server/subtasks.js): a summary of its subtasks -- its dates, %, status, arrows and SOV dollars are theirs.
  const kidCount = allTasks.filter((x) => Number(x.parent_task_id) === Number(taskId)).length || Number(task && task.subtask_count) || 0;
  const predecessors = task?.predecessors || [];
  const successors = allTasks.filter(t =>
    (t.predecessors || []).some(p => Number(p.predecessor_task_id) === Number(taskId)),
  );
  const disable = saving || deleting;
  // The sub's progress on E3 Subs when it isn't this task's % (2026-10-28, Duncan: "it should also show that in the task
  // properties pop-up window"; subs/SubProgressBanner.js): a mark on the slider and a note under it. Use moves the slider
  // -- as much as the dates above allow by the billing date (or today), worked out as they change; Save saves it.
  const sub = subProgress && Number.isFinite(subProgress.sub_pct) && Math.round(Number(pct) || 0) !== subProgress.sub_pct ? subProgress : null;
  const subLower = !!sub && sub.sub_pct < (Number(pct) || 0);
  // the dates Save sends (the scheduler's, as it works them out) -- what the server caps the % by
  const subStart = (preview && preview.start_date) || formData.start_date;
  const subFinish = (preview && preview.finish_date) || formData.finish_date;
  const subMax = sub ? maxPctOf(subStart, subFinish, sub.ref_day) : 100;
  const subTarget = sub ? Math.min(sub.sub_pct, subMax) : null;
  const subUsable = !!sub && (subLower ? subTarget < (Number(pct) || 0) : subTarget > (Number(pct) || 0));
  // The schedule holds some of the sub's % back: explained in plain words (Duncan 2026-10-28: "Really think about a
  // good way to explain this to a user") -- the schedule's clock by the billing date beside the sub's %, and which date
  // to change (with the latest finish that lets it all in).
  const subHeld = !!sub && !subLower && subMax < sub.sub_pct;
  const subFinishBy = subHeld && subMax > 0 ? finishForPct(subStart, sub.ref_day, sub.sub_pct) : null;
  const subBy = sub ? (sub.ref_is_draft ? `${dayOf(sub.ref_day)}, the billing date` : `today, ${dayOf(sub.ref_day)}`) : '';
  // Led here from the banner: the date that lets more of the sub's % in -- the start when the task hasn't begun by then,
  // else the finish -- outlined while it still holds the % back.
  const leadTo = focus === 'progress' && subHeld ? (subMax === 0 ? 'start' : 'finish') : null;
  // The hints, from subWork: start it the day the work started, when that's before the schedule's start; then (the sub
  // says 100%) finish it the day the last line was marked done, when that's before the schedule's finish. Each on the
  // first work day on or after it, as the schedule places it (a sub can work a Saturday; a Mon-Fri job can't start or
  // finish on one -- Duncan 2026-10-28 hit a Saturday hint the schedule moved to Monday, and the hint stayed up); a hint
  // goes once it's been used.
  const day10 = (v) => String(v || '').slice(0, 10);
  const subWeek = String((preview && preview.work_week) || workWeek || '5') === '7' ? '7' : '5';
  const workDayFrom = (ymd) => {
    if (!ymd || subWeek === '7') return ymd;
    const d = new Date(`${ymd}T12:00:00Z`);
    while (d.getUTCDay() === 0 || d.getUTCDay() === 6) d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
  };
  const weekdayOf = (ymd) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
  const firstWork = subWork && subWork.first ? workDayFrom(subWork.first) : null;
  const lastWork = subWork && subWork.last ? workDayFrom(subWork.last) : null;
  const startHint = subHeld && firstWork && subStart && firstWork < day10(subStart) && formData.constraint_date !== firstWork ? firstWork : null;
  const finishHint = subHeld && !startHint && subWork && !subWork.docs && sub.sub_pct >= 100 && lastWork && subFinish
    && lastWork < day10(subFinish) && lastWork >= day10(subStart) ? lastWork : null;
  const offDay = (real, used) => (real !== used ? ` That was a ${weekdayOf(real)}, and work days are Mon–Fri, so the schedule can use ${dayOf(used)}.` : '');
  const leadRing = { boxShadow: '0 0 0 2px var(--warning)', borderRadius: 9 };
  const liveStatus = formData.status || 'Not Started';
  const typeColor = TYPE_COLOR[formData.task_type] || 'var(--text-3)';

  // A parent's span, as its Schedule section shows it (its subtasks' working days, the job's calendar).
  const parentDays = kidCount > 0 && formData.start_date && formData.finish_date
    ? workSpan(formData.start_date, formData.finish_date, String(workWeek || '5')) : null;
  const holdsDollars = kidCount > 0 && ((task && task.sov_allocations && task.sov_allocations.length > 0)
    || !!(allTasks.find((x) => Number(x.id) === Number(taskId)) || {}).split_pending);

  return (
    <>
      {lowWarn && <SplitDown queue={lowWarn.ids} mode="moment" onChanged={() => { if (onSaved) onSaved(); }} onClose={() => { const a = lowWarn.after; setLowWarn(null); if (a) a(); }} />}
      <div
        onClick={!disable ? onClose : undefined}
        style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'oklch(0.3 0.02 90 / 0.42)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          paddingTop: 'max(28px, 5vh)',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 580, maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'calc(100vh - 56px)',
            display: 'flex', flexDirection: 'column',
            background: 'var(--bg-surface)',
            borderRadius: 16,
            border: '1px solid var(--border-subtle)',
            boxShadow: '0 20px 50px -10px oklch(0.2 0.02 90 / 0.25), 0 8px 20px oklch(0.2 0.02 90 / 0.08)',
            overflow: 'hidden',
          }}
        >
          {/* ─────────── Header ─────────── */}
          <div style={{
            padding: '18px 24px 16px',
            borderBottom: '1px solid var(--border-subtle)',
            background: 'var(--bg-surface)',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Pill
                color={typeColor}
                bg={`color-mix(in oklch, ${typeColor} 12%, transparent)`}
                dot
              >
                {formData.task_type || 'Engineering'}
              </Pill>
              {formData.task_code && (
                <span className="mono" style={{ fontSize: 13.5, color: 'var(--text-3)', fontWeight: 500 }}>
                  {formData.task_code}
                </span>
              )}
              <Pill
                color={STATUS_META[liveStatus]?.color || 'var(--text-3)'}
                bg={STATUS_META[liveStatus]?.bg || 'var(--bg-surface-2)'}
                dot
              >
                {liveStatus}
              </Pill>
              <div style={{ flex: 1 }} />
              <button
                type="button"
                onClick={onClose}
                disabled={disable}
                aria-label="Close"
                style={{
                  width: 30, height: 30, borderRadius: 7,
                  background: 'transparent', border: 'none',
                  color: 'var(--text-3)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  cursor: disable ? 'not-allowed' : 'pointer',
                  opacity: disable ? 0.4 : 1,
                }}
                onMouseEnter={(e) => { if (!disable) { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-1)'; } }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-3)'; }}
              >
                <XIcon />
              </button>
            </div>
            <h2 style={{
              margin: 0, fontSize: 19.5, fontWeight: 600,
              color: 'var(--text-1)', letterSpacing: '-0.018em', lineHeight: 1.3,
              wordBreak: 'break-word',
            }}>{formData.task_name || 'Task Properties'}</h2>
          </div>

          {/* ─────────── Body (scrollable) ─────────── */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 24px 8px' }}>
            {loading ? (
              <div style={{
                height: 360, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 12,
                color: 'var(--text-3)',
              }}>
                <Spinner size={22} color="var(--accent)" />
                <div style={{ fontSize: 14 }}>Loading task…</div>
              </div>
            ) : (
              <SectionCards>
                {/* GENERAL */}
                <SectionHeader title="General" first />
                <div style={{ marginBottom: 12 }}>
                  <Label>Task Name</Label>
                  <input
                    type="text" name="task_name"
                    value={formData.task_name}
                    onChange={handleChange}
                    placeholder="Untitled task"
                    style={inputBaseStyle}
                    onFocus={onFocusRing} onBlur={onBlurRing}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <Label>Description</Label>
                  <textarea
                    name="task_description" rows={2}
                    value={formData.task_description}
                    onChange={handleChange}
                    placeholder="Describe the scope of this task…"
                    style={{
                      ...inputBaseStyle,
                      height: 'auto', minHeight: 56, padding: '8px 10px',
                      resize: 'vertical', lineHeight: 1.45,
                    }}
                    onFocus={onFocusRing} onBlur={onBlurRing}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <Label>Building Area / Phase</Label>
                    <input
                      type="text" name="building_area_phase"
                      value={formData.building_area_phase}
                      onChange={handleChange}
                      placeholder="e.g. Building A · Foundations"
                      style={inputBaseStyle}
                      onFocus={onFocusRing} onBlur={onBlurRing}
                    />
                  </div>
                  <div>
                    <Label>Task Type</Label>
                    <select
                      name="task_type"
                      value={formData.task_type}
                      onChange={handleChange}
                      style={{ ...inputBaseStyle, paddingRight: 28, cursor: 'pointer' }}
                      onFocus={onFocusRing} onBlur={onBlurRing}
                    >
                      {TASK_TYPES.map(tt => <option key={tt} value={tt}>{tt}</option>)}
                    </select>
                  </div>
                </div>
                {/* Client Visible + Milestone, up from Options (Duncan 2026-11-02: "move those other 2 checks up to general as
                    well"), with the milestone's color under them */}
                <div style={{ display: 'flex', gap: 28, marginBottom: 12 }}>
                  <Check
                    checked={!!formData.client_visible}
                    onChange={(e) => handleChange({ target: { name: 'client_visible', type: 'checkbox', checked: e.target.checked } })}
                    label="Client Visible"
                    disabled={disable}
                  />
                  {kidCount === 0 && ( /* a task with subtasks can't be a milestone */
                  <Check
                    checked={!!formData.milestone_flag}
                    onChange={(e) => handleChange({ target: { name: 'milestone_flag', type: 'checkbox', checked: e.target.checked } })}
                    label="Milestone"
                    disabled={disable}
                  />
                  )}
                </div>
                {kidCount === 0 && !!formData.milestone_flag && (
                  <MilestoneColorPicker value={formData.milestone_color} disabled={disable}
                    onChange={(c) => setFormData((f) => ({ ...f, milestone_color: c }))} />
                )}
                {(() => { // Subtask of (2026-10-29): one of its section's tasks, one level deep; then where it goes (SubtaskPlacement.js)
                  const opts = allTasks.filter((x) => x.id !== taskId && x.parent_task_id == null && (x.building_area_phase || '') === (formData.building_area_phase || '') && !Number(x.milestone_flag));
                  const val = formData.parent_task_id && opts.some((o) => o.id === Number(formData.parent_task_id)) ? String(formData.parent_task_id) : '';
                  return (
                    <div style={{ marginBottom: 12 }}>
                      <Label>Subtask of</Label>
                      <select name="parent_task_id" value={val} disabled={kidCount > 0} onChange={(e) => { setArrowPicks({}); setFormData((f) => ({ ...f, parent_task_id: e.target.value ? Number(e.target.value) : null })); }}
                        style={{ ...inputBaseStyle, paddingRight: 28, cursor: kidCount > 0 ? 'default' : 'pointer' }} onFocus={onFocusRing} onBlur={onBlurRing}>
                        <option value="">None: it's a task</option>
                        {opts.map((x) => <option key={x.id} value={String(x.id)}>{x.task_code} · {x.task_name}</option>)}
                      </select>
                      <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
                        {kidCount > 0 ? `It has ${kidCount} subtask${kidCount === 1 ? '' : 's'}, so it stays a task: its dates and % come from them.` : "A subtask's dates and % roll up into its task. Its SOV links and its work are its own."}
                      </div>
                      {/* "+ Add a subtask" (Duncan 2026-11-02): this window closes and Add Task opens, already a subtask of this
                          task (ScheduleView). A task only: not a subtask, a milestone, or one with fixture lines. */}
                      {onAddSubtask && !disable && !val && task && task.parent_task_id == null && !Number(task.milestone_flag) && !formData.milestone_flag
                        && !(allTasks.find((x) => x.id === Number(taskId)) || {}).has_work && (
                        <button type="button" onClick={addSubtaskHere}
                          style={{ marginTop: 10, fontSize: 13, fontWeight: 600, padding: '4px 12px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
                            color: 'var(--accent-text)', background: 'color-mix(in oklch, var(--accent) 10%, var(--bg-surface))', border: '1px solid color-mix(in oklch, var(--accent) 40%, transparent)' }}>
                          + Add a subtask to {formData.task_name || 'this task'}
                        </button>
                      )}
                      {val && (() => {
                        const ap = opts.find((o) => o.id === Number(val));
                        const me = { id: Number(taskId), task_name: formData.task_name, start_date: (preview && preview.start_date) || formData.start_date, finish_date: (preview && preview.finish_date) || formData.finish_date };
                        const sibs = allTasks.filter((x) => Number(x.parent_task_id) === ap.id);
                        return <SubtaskPlacement parent={ap} siblings={sibs} meLabel="This task" me={me}
                          offers={arrowOffers({ parent: ap, siblings: sibs, me, allTasks, isNew: false })} picks={arrowPicks}
                          onPick={(k, v) => setArrowPicks((pk) => ({ ...pk, [k]: v }))}
                          handed={Number(task && task.parent_task_id) === ap.id ? null : handedArrows({ parent: ap, siblings: sibs, me, allTasks })} />;
                      })()}
                    </div>
                  );
                })()}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <Label>Status</Label>
                    <select
                      name="status"
                      value={formData.status || 'Not Started'}
                      onChange={handleStatusChange}
                      disabled={kidCount > 0}
                      title={kidCount > 0 ? 'It follows its subtasks’ %' : undefined}
                      style={{ ...inputBaseStyle, paddingRight: 28, cursor: kidCount > 0 ? 'default' : 'pointer' }}
                      onFocus={onFocusRing} onBlur={onBlurRing}
                    >
                      <option>Not Started</option>
                      <option>In Progress</option>
                      <option>Complete</option>
                    </select>
                  </div>
                  <div>
                    <Label>Responsible Party</Label>
                    <ResponsiblePartyField
                      value={formData.responsible_party}
                      subcontractorId={formData.subcontractor_id}
                      onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
                      inputStyle={inputBaseStyle}
                      onFocus={onFocusRing} onBlur={onBlurRing}
                    />
                    {kidCount > 0 && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>Each subtask has its own sub. A sub picked here goes to all of them.</div>}
                  </div>
                </div>

                {/* SCHEDULE — Microsoft Project-style (#50–#52): the start comes from the
                    predecessors (typing one pins it), the duration is typed and the finish is
                    calculated. Save recalculates everything after this task. */}
                <div ref={scheduleRef} />
                <SectionHeader title="Schedule" />
                {kidCount > 0 ? (
                  <div style={{ fontSize: 14, color: 'var(--text-1)', lineHeight: 1.5 }}>
                    <span className="mono">{fmtDate(formData.start_date)} → {fmtDate(formData.finish_date)}</span>
                    {parentDays ? <span style={{ color: 'var(--text-3)' }}> · {parentDays} days</span> : null}
                    <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 4 }}>From its {kidCount} subtask{kidCount === 1 ? '' : 's'}: it starts with the first and finishes with the last. Change a subtask's dates to move it.</div>
                  </div>
                ) : (() => {
                  const pinned = formData.constraint_type === 'SNET' && !!formData.constraint_date;
                  const driverName = preview && preview.driver && typeof preview.driver === 'object' ? preview.driver.task_name : null;
                  const shownStart = pinned ? formData.constraint_date : (linked && preview && preview.start_date) || formData.start_date;
                  const linkWins = pinned && preview && preview.start_date && preview.start_date > formData.constraint_date;
                  const milestone = !!formData.milestone_flag;
                  const sevenDay = String((preview && preview.work_week) || workWeek || '5') === '7';
                  const shownFinish = (preview && preview.finish_date) || formData.finish_date;
                  const datePicker = (mode) => (
                    <DateRangePicker
                      key={mode} mode={mode} start={shownStart} finish={shownFinish} workWeek={sevenDay ? '7' : '5'}
                      anchorRef={mode === 'start' ? startRef : finishRef}
                      onStart={(iso) => handleStartTyped({ target: { value: iso } })}
                      onFinish={handleFinishPicked}
                      onClose={() => setPicker(null)}
                      align={mode === 'start' ? 'left' : 'right'}
                    />
                  );
                  const moves = schedDirty && preview ? preview.moves : 0;
                  // The tasks this change moves, where they are and where they land (2026-10-28, Duncan: "There's nothing
                  // that shows us what that does to the rest of the dates if it has tasks that are dependent on it").
                  const moved = schedDirty && preview && Array.isArray(preview.moved) ? preview.moved : [];
                  // Later than the pin with no link driving it: the pinned day isn't a work day (a Saturday on a Mon-Fri job), so
                  // it starts on the next one -- said so, not as a warning (2026-10-28).
                  const dayOff = linkWins && !driverName;
                  const caption = pinned
                    ? (dayOff
                      ? <>{fmtDate(formData.constraint_date)} isn't a work day, so it starts {fmtDate(preview.start_date)}</>
                      : linkWins
                      ? <>Can't start before {fmtDate(preview.start_date)}{driverName ? <> — {driverName} comes first</> : null}</>
                      : <>Pinned: starts no earlier than {fmtDate(formData.constraint_date)}</>)
                    : driverName ? <>From {driverName}</> : null;
                  return (
                    <>
                      {/* Start · Duration · Finish; on a phone Finish drops to its own line. Start and Finish open one
                          calendar (DateRangePicker): click the start, then the finish, with the days counted as you go. */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 6 }}>
                        <div style={{ flex: '1 1 150px', minWidth: 0 }}>
                          <Label>Start</Label>
                          {/* The calendar hangs from the field itself, so a caption appearing under it doesn't move it. */}
                          <div style={{ position: 'relative', ...(leadTo === 'start' ? leadRing : null) }}>
                            <input
                              ref={startRef}
                              type="date" name="start_date" className="tsk-date"
                              value={shownStart || ''}
                              onChange={handleStartTyped}
                              onClick={() => { if (!milestone && !disable) setPicker('start'); }}
                              style={{ ...inputBaseStyle, fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em', cursor: 'pointer' }}
                              onFocus={onFocusRing} onBlur={onBlurRing}
                            />
                            {picker === 'start' && !milestone && datePicker('start')}
                          </div>
                          {caption && (
                            <div style={{ fontSize: 12, color: linkWins && !dayOff ? 'var(--warning)' : 'var(--text-3)', marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'baseline' }}>
                              <span>{caption}</span>
                              {pinned && (
                                <button
                                  type="button" onClick={removePin} disabled={disable}
                                  style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', fontSize: 12, color: 'var(--accent-text)', cursor: 'pointer', textDecoration: 'underline' }}
                                >
                                  Remove pin
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <div style={{ flex: '0 0 110px' }}>
                          <Label>Duration</Label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text" inputMode="numeric" name="duration"
                              value={milestone ? '1' : formData.duration}
                              onChange={handleDurationChange}
                              disabled={milestone || disable}
                              style={{ ...inputBaseStyle, paddingRight: 40, fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em' }}
                              onFocus={onFocusRing} onBlur={onBlurRing}
                            />
                            <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12.5, color: 'var(--text-3)', pointerEvents: 'none' }}>days</span>
                          </div>
                        </div>
                        <div style={{ flex: '1 1 150px', minWidth: 0 }}>
                          <Label>Finish</Label>
                          {milestone ? (
                            <div style={{
                              ...inputBaseStyle,
                              display: 'flex', alignItems: 'center', gap: 6,
                              background: 'var(--bg-surface-2)',
                              border: '1px dashed var(--border-default)',
                              color: 'var(--text-2)',
                              fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em',
                            }}>
                              <span style={{ flex: 1 }}>{fmtDate(shownFinish) || '—'}</span>
                              <LockIcon />
                            </div>
                          ) : (
                            <div style={{ position: 'relative', ...(leadTo === 'finish' ? leadRing : null) }}>
                            <button
                              ref={finishRef} type="button" disabled={disable}
                              onClick={() => setPicker((p) => (p === 'finish' ? null : 'finish'))}
                              aria-haspopup="dialog" aria-expanded={picker === 'finish'}
                              style={{
                                ...inputBaseStyle,
                                display: 'flex', alignItems: 'center', gap: 6, textAlign: 'left', cursor: disable ? 'default' : 'pointer',
                                borderColor: picker === 'finish' ? 'var(--accent)' : undefined,
                                fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em',
                              }}
                            >
                              <span style={{ flex: 1 }}>{fmtDate(shownFinish) || '—'}</span>
                              <CalendarIcon />
                            </button>
                            {picker === 'finish' && datePicker('finish')}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                        <span>{milestone ? 'Milestone: one day' : sevenDay ? 'Calendar days, 7-day week' : 'Working days, Mon–Fri'}</span>
                        {moves > 0 && !moved.length && <span style={{ color: 'var(--accent-text)' }}>Saving moves {moves} later task{moves === 1 ? '' : 's'}</span>}
                      </div>
                      {moved.length > 0 && (
                        <div style={{
                          marginBottom: 12, padding: '10px 12px', borderRadius: 9,
                          background: 'var(--accent-soft)', border: '1px solid color-mix(in oklch, var(--accent) 30%, transparent)',
                        }}>
                          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--accent-text)' }}>
                            Saving moves {moved.length} later task{moved.length === 1 ? '' : 's'} with it
                          </div>
                          {moved.slice(0, 8).map((m) => (
                            <div key={m.id} style={{ padding: '6px 0', borderTop: '1px solid var(--border-subtle)', marginTop: 6, fontSize: 12.5 }}>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', minWidth: 0 }}>
                                <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-3)', flex: '0 0 auto' }}>{m.task_code}</span>
                                <span style={{ color: 'var(--text-1)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {m.task_name}
                                  {m.after && <span style={{ color: 'var(--text-3)' }}> · after {Number(m.after.id) === Number(taskId) ? 'this one' : m.after.task_name}</span>}
                                </span>
                              </div>
                              <div style={{ marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                <span style={{ color: 'var(--text-3)', textDecoration: 'line-through', marginRight: 6 }}>{fmtRange(m.from_start, m.from_finish)}</span>
                                <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{fmtRange(m.start_date, m.finish_date)}</span>
                              </div>
                            </div>
                          ))}
                          {moved.length > 8 && <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 6 }}>and {moved.length - 8} more</div>}
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* COMPLETION */}
                {/* Not on a task with subtasks: its % is read-only, from them (the subtasks video, 2026-11-02). */}
                <SectionHeader title="Completion" right={
                  liveStatus === 'In Progress' && !kidCount ? (
                    <span className="mono" style={{ fontSize: 12, color: 'var(--text-3)' }}>Auto-syncs to status</span>
                  ) : null
                } />
                {kidCount > 0 ? (
                  <div style={{ fontSize: 14, color: 'var(--text-1)' }}>
                    <span className="mono" style={{ fontWeight: 600 }}>{Math.round(Number(pct) || 0)}%</span>
                    <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 4 }}>Its subtasks' %, counted by their working days. Change a subtask's % to move it.</div>
                  </div>
                ) : (<>
                <CompletionSlider value={pct} onChange={setCompletion} disabled={disable} mark={sub ? sub.sub_pct : null} floor={floorPct || null} />
                {floorPct > 0 && (
                  pct <= floorPct + 1e-9 ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 6, fontSize: 13.5, lineHeight: 1.45, color: 'var(--text-2)' }}>
                      <span style={{ color: 'var(--warning)', flexShrink: 0, marginTop: 2 }}><LockIcon size={13} /></span>
                      <span>
                        <span style={{ fontWeight: 600, color: 'var(--text-1)' }}>{formData.task_name || 'This task'} can't go below {floorPct}%.</span>{' '}
                        {task.billing_floor.pay_app} billed {fmtMoney(task.billing_floor.amount)} on {task.billing_floor.line}, and a finalized pay app can't be billed backwards.
                      </span>
                    </div>
                  ) : (
                    <div className="mono" style={{ textAlign: 'right', marginTop: 2, fontSize: 12, color: 'var(--warning)' }}>
                      Billed {floorPct}% · {task.billing_floor.pay_app}
                    </div>
                  )
                )}
                {sub && (
                  <div style={{
                    marginTop: 10, padding: '12px 14px', borderRadius: 10,
                    background: 'var(--warning-soft)', border: '1px solid color-mix(in oklch, var(--warning) 35%, transparent)',
                  }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--warning)' }}>
                      {sub.sub_name} says this is {sub.sub_pct}% done{basisOf(sub) ? ` (${basisOf(sub)})` : ''}
                    </div>
                    {subHeld && (
                      <>
                        <SubLane label={`Schedule by ${dayOf(sub.ref_day)}`} pct={subMax} marker />
                        <div style={{ display: 'grid', gridTemplateColumns: '118px minmax(0, 1fr) 44px', gap: 8, marginTop: 3, fontSize: 11.5, color: 'var(--text-3)' }}>
                          <span />
                          <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                            <span>{dayOf(subStart)} start</span><span>{dayOf(subFinish)} finish</span>
                          </span>
                          <span />
                        </div>
                        <SubLane label="Sub says" pct={sub.sub_pct} sub />
                      </>
                    )}
                    <div style={{ fontSize: 13, color: 'var(--text-1)', lineHeight: 1.55, marginTop: 10 }}>
                      {subLower ? (
                        <><b style={{ fontWeight: 600 }}>Behind the schedule.</b> The sub reports less than the {Number(pct) || 0}% here. Take {subTarget}% to match it, or keep yours.</>
                      ) : !subHeld ? (
                        <><b style={{ fontWeight: 600 }}>The schedule allows it.</b> Take {subTarget}% to match the sub.</>
                      ) : subMax === 0 ? (
                        <><b style={{ fontWeight: 600 }}>Billing follows the schedule.</b> This task isn’t scheduled to start until {dayOf(subStart)}, so nothing can be billed for it by {subBy}.</>
                      ) : (
                        <><b style={{ fontWeight: 600 }}>Billing follows the schedule.</b> By {subBy}, {subMax}% of this task’s scheduled time has passed, so {subMax}% is the most you can bill for it.</>
                      )}
                    </div>
                    {subHeld && (
                      <div style={{ fontSize: 13, color: 'var(--text-1)', lineHeight: 1.55, marginTop: 6 }}>
                        {subMax === 0 ? (
                          <>If the work really started early, set the <b style={{ fontWeight: 600 }}>start date</b> above to when it began.</>
                        ) : (
                          <>If the work really finished early, set the <b style={{ fontWeight: 600 }}>finish date</b> above to when it was done.
                            {subFinishBy ? ` On or before ${dayOf(subFinishBy)} lets you take all ${sub.sub_pct}%.` : ''} Otherwise, take {subMax}% now and the rest as the days pass.</>
                        )}
                      </div>
                    )}
                    {startHint && (
                      <SubHint
                        title={`The work started ${dayOf(subWork.first)}.`}
                        text={`${subWork.docs
                          ? 'That’s the day the sub added its first document to this task.'
                          : `That’s the first day the sub marked a fixture done on this task${subWork.firstBy ? ` (${subWork.firstBy})` : ''}.`}${offDay(subWork.first, startHint) || ' Starting it then counts the time since.'}`}
                        button={`Start it ${dayOf(startHint)}`}
                        disabled={disable}
                        onClick={() => handleStartTyped({ target: { value: startHint } })}
                      />
                    )}
                    {finishHint && (
                      <SubHint
                        title={`The work finished ${dayOf(subWork.last)}.`}
                        text={`That’s the day the sub marked its last fixture done${subWork.lastBy ? ` (${subWork.lastBy})` : ''}.${offDay(subWork.last, finishHint)} ${maxPctOf(subStart, finishHint, sub.ref_day) >= 100
                          ? 'Finishing it then lets you take all 100%.' : 'Finishing it then matches the schedule to the work.'}`}
                        button={`Finish it ${dayOf(finishHint)}`}
                        disabled={disable}
                        onClick={() => handleFinishPicked(finishHint, workSpan(subStart, finishHint, subWeek))}
                      />
                    )}
                    {subUsable && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                        <PrimaryButton disabled={disable} onClick={() => setCompletion(subTarget)} style={{ height: 30, fontSize: 13, padding: '0 12px' }}>
                          Take {subTarget}%
                        </PrimaryButton>
                      </div>
                    )}
                  </div>
                )}
                </>)}

                {/* PREDECESSORS */}
                <SectionHeader title="Predecessors" right={
                  <span className="mono" style={{ fontSize: 12.5, color: predecessors.length === 0 ? 'var(--text-4)' : 'var(--text-3)' }}>
                    {predecessors.length}
                  </span>
                } />
                {kidCount > 0 ? <EmptyRow text="Arrows go on its subtasks, never on it: its dates come from them" /> : (<>
                {predecessors.length === 0 && !showAddPred && (
                  <EmptyRow text="No predecessors linked" />
                )}
                {predecessors.map(pred => {
                  const predTask = allTasks.find(tt => tt.id === pred.predecessor_task_id);
                  const lag = pred.lag_days || 0;
                  return (
                    <div key={pred.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 12px',
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 8,
                      marginBottom: 8,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
                          <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)', whiteSpace: 'nowrap', flexShrink: 0 }}>{predTask?.task_code}</span>
                          <span style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{predTask?.task_name}</span>
                        </div>
                        {predTask && (predTask.start_date || predTask.finish_date) && (
                          <div className="mono" style={{ fontSize: 12, color: 'var(--text-4)' }}>
                            {predTask.start_date ? `Start: ${fmtDate(predTask.start_date)}` : ''}
                            {predTask.start_date && predTask.finish_date ? <span style={{ margin: '0 4px' }}>→</span> : null}
                            {predTask.finish_date ? `Finish: ${fmtDate(predTask.finish_date)}` : ''}
                          </div>
                        )}
                      </div>
                      <select
                        value={pred.dependency_type || 'FS'}
                        onChange={(e) => handlePredUpdate(pred.id, { dependency_type: e.target.value, lag_days: lag })}
                        style={{
                          height: 28, padding: '0 8px',
                          background: 'var(--bg-surface)',
                          border: '1px solid var(--border-default)', borderRadius: 6,
                          fontFamily: 'var(--font-mono)', fontSize: 13.5, fontWeight: 500, color: 'var(--text-1)',
                          cursor: 'pointer', outline: 'none',
                        }}
                        title="Dependency type"
                      >
                        {DEP_TYPES.map(d => <option key={d.value} value={d.value}>{d.short}</option>)}
                      </select>
                      <LagInput
                        value={lag}
                        onCommit={(n) => handlePredUpdate(pred.id, { dependency_type: pred.dependency_type || 'FS', lag_days: n })}
                        style={{
                          width: 56, height: 28, padding: '0 8px',
                          background: 'var(--bg-surface)',
                          border: '1px solid var(--border-default)', borderRadius: 6,
                          fontFamily: 'var(--font-mono)', fontSize: 13.5, color: lag === 0 ? 'var(--text-4)' : 'var(--text-1)',
                          textAlign: 'right', outline: 'none',
                        }}
                      />
                      <select
                        value={pred.lag_elapsed ? 'ed' : 'd'}
                        onChange={(e) => handlePredUpdate(pred.id, { dependency_type: pred.dependency_type || 'FS', lag_days: lag, lag_elapsed: e.target.value === 'ed' })}
                        style={{
                          height: 28, padding: '0 8px',
                          background: 'var(--bg-surface)',
                          border: '1px solid var(--border-default)', borderRadius: 6,
                          fontSize: 13, color: 'var(--text-1)',
                          cursor: 'pointer', outline: 'none',
                        }}
                        title={LAG_UNIT_TITLE}
                      >
                        {LAG_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                      </select>
                      <button
                        type="button"
                        onClick={() => handlePredRemove(pred.id)}
                        aria-label="Remove predecessor"
                        style={{
                          width: 24, height: 24, borderRadius: 5,
                          background: 'transparent', border: 'none', color: 'var(--danger)',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--danger-soft)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <XIcon size={13} />
                      </button>
                    </div>
                  );
                })}

                {predError && (
                  <div role="alert" style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    padding: '8px 12px 8px 14px', marginBottom: 8, borderRadius: 8,
                    background: 'var(--danger-soft)',
                    boxShadow: 'inset 3px 0 0 var(--danger)',
                    border: '1px solid color-mix(in oklch, var(--danger) 18%, transparent)',
                    color: 'var(--danger)', fontSize: 13.5, lineHeight: 1.45,
                  }}>
                    <AlertCircleIcon />
                    <span style={{ flex: 1 }}>{predError}</span>
                  </div>
                )}
                {showAddPred ? (
                  <div style={{
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 10,
                    padding: 12,
                    boxShadow: 'var(--shadow-sm)',
                    marginBottom: 4,
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '170px auto 1fr', gap: 8, marginBottom: 10 }}>
                      <select
                        value={addPredType}
                        onChange={(e) => setAddPredType(e.target.value)}
                        style={{ ...inputBaseStyle, paddingRight: 28, cursor: 'pointer' }}
                        onFocus={onFocusRing} onBlur={onBlurRing}
                      >
                        {DEP_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                      </select>
                      <div style={{
                        display: 'flex', alignItems: 'center',
                        height: 34, padding: '0 10px',
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border-default)', borderRadius: 8,
                      }}>
                        <input
                          type="number"
                          value={addPredLag}
                          onChange={(e) => setAddPredLag(parseInt(e.target.value, 10) || 0)}
                          style={{
                            // A set width: the box is as wide as its contents now that the unit
                            // picker sits in it (#55), so a 100% width would collapse to nothing.
                            flex: '0 0 auto', width: 44,
                            background: 'transparent', border: 'none', outline: 'none',
                            fontFamily: 'var(--font-mono)', fontSize: 14.5, color: 'var(--text-1)',
                            letterSpacing: '-0.02em',
                          }}
                        />
                        <select
                          value={addPredElapsed ? 'ed' : 'd'}
                          onChange={(e) => setAddPredElapsed(e.target.value === 'ed')}
                          style={{
                            background: 'transparent', border: 'none', outline: 'none',
                            color: 'var(--text-3)', fontSize: 12.5, cursor: 'pointer', padding: 0,
                          }}
                          title={LAG_UNIT_TITLE}
                        >
                          {LAG_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                        </select>
                        <span style={{ color: 'var(--text-3)', fontSize: 12.5, marginLeft: 4 }}>lag</span>
                      </div>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        height: 34, padding: '0 10px',
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border-default)', borderRadius: 8,
                        color: 'var(--text-3)',
                      }}>
                        <SearchIcon />
                        <input
                          ref={predSearchRef}
                          type="text" autoFocus
                          value={predSearch}
                          onChange={(e) => setPredSearch(e.target.value)}
                          placeholder="Search tasks…"
                          style={{
                            flex: 1, minWidth: 0, height: '100%',
                            background: 'transparent', border: 'none', outline: 'none',
                            fontSize: 14.5, color: 'var(--text-1)',
                          }}
                          onFocus={(e) => { e.currentTarget.parentElement.style.borderColor = 'var(--accent)'; e.currentTarget.parentElement.style.boxShadow = '0 0 0 2px var(--accent-ring)'; }}
                          onBlur={(e) => { e.currentTarget.parentElement.style.borderColor = 'var(--border-default)'; e.currentTarget.parentElement.style.boxShadow = 'none'; }}
                        />
                      </div>
                    </div>
                    <div style={{
                      maxHeight: 160, overflowY: 'auto',
                      background: 'var(--bg-surface-2)',
                      border: '1px solid var(--border-subtle)', borderRadius: 8,
                    }}>
                      {(() => {
                        const existing = new Set(predecessors.map(p => p.predecessor_task_id));
                        const q = predSearch.toLowerCase().trim();
                        // never a parent: arrows go between bottom rows (server/subtasks.js) -- a task with subtasks offers them
                        const parents = new Set(allTasks.filter((x) => x.parent_task_id != null).map((x) => Number(x.parent_task_id)));
                        const candidates = allTasks.filter(ct =>
                          ct.id !== taskId &&
                          !existing.has(ct.id) &&
                          !parents.has(Number(ct.id)) &&
                          (q === '' || (ct.task_name || '').toLowerCase().includes(q) || (ct.task_code || '').toLowerCase().includes(q)),
                        ).slice(0, 30);
                        if (candidates.length === 0) {
                          return <div style={{ padding: '12px 14px', fontSize: 13.5, color: 'var(--text-4)', textAlign: 'center' }}>No matching tasks</div>;
                        }
                        return candidates.map((ct, i) => (
                          <div
                            key={ct.id}
                            onClick={() => !addingPred && handleAddPredecessor(ct.id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '8px 10px',
                              borderBottom: i < candidates.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                              cursor: addingPred ? 'wait' : 'pointer',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)', minWidth: 70 }}>{ct.task_code}</span>
                            <span style={{ fontSize: 14, color: 'var(--text-1)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ct.task_name}</span>
                            {ct.finish_date && (
                              <span className="mono" style={{ fontSize: 12, color: 'var(--text-4)' }}>{fmtDate(ct.finish_date)}</span>
                            )}
                          </div>
                        ));
                      })()}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                      <button
                        type="button"
                        onClick={() => { setShowAddPred(false); setPredSearch(''); setPredError(null); }}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '0 14px', height: 32, borderRadius: 8,
                          fontSize: 14, fontWeight: 500, color: 'var(--text-2)',
                          background: 'var(--bg-surface)',
                          border: '1px solid var(--border-default)',
                          cursor: 'pointer',
                        }}
                      >Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: 4, marginBottom: 4 }}>
                    <ChipBtn onClick={() => { setShowAddPred(true); setPredError(null); }} disabled={disable}>Add Predecessor</ChipBtn>
                  </div>
                )}
                </>)}

                {/* DEPENDENT TASKS -- none on a parent (its subtasks carry the arrows) */}
                {kidCount === 0 && <SectionHeader title="Dependent Tasks" right={
                  <span className="mono" style={{ fontSize: 12.5, color: successors.length === 0 ? 'var(--text-4)' : 'var(--text-3)' }}>
                    {successors.length}
                  </span>
                } />}
                {kidCount === 0 && (successors.length === 0 ? (
                  <EmptyRow text="No tasks depend on this task" />
                ) : successors.map(s => {
                  const pred = (s.predecessors || []).find(p => Number(p.predecessor_task_id) === Number(taskId));
                  const depShort = DEP_TYPES.find(d => d.value === pred?.dependency_type)?.short || pred?.dependency_type || 'FS';
                  const lag = pred?.lag_days || 0;
                  return (
                    <div key={s.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 12px',
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 8,
                      marginBottom: 8,
                    }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: 6,
                        background: 'var(--accent-soft)',
                        color: 'var(--accent-text)',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <ArrowRightIcon />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
                          <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)' }}>{s.task_code}</span>
                          <span style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.task_name}</span>
                        </div>
                        {(s.start_date || s.finish_date) && (
                          <div className="mono" style={{ fontSize: 12, color: 'var(--text-4)' }}>
                            {s.start_date ? `Start: ${fmtDate(s.start_date)}` : ''}
                            {s.start_date && s.finish_date ? <span style={{ margin: '0 4px' }}>→</span> : null}
                            {s.finish_date ? `Finish: ${fmtDate(s.finish_date)}` : ''}
                          </div>
                        )}
                      </div>
                      <Pill color="var(--accent-text)" bg="var(--accent-soft)">
                        <span className="mono" style={{ letterSpacing: '-0.01em' }}>{depShort}</span>
                        {lag !== 0 && <span className="mono" style={{ opacity: 0.75 }}>{lag > 0 ? '+' : ''}{lag}{pred?.lag_elapsed ? 'ed' : 'd'}</span>}
                      </Pill>
                    </div>
                  );
                }))}

                {/* SOV LINKS */}
                <SectionHeader title="SOV Links" />
                {kidCount > 0 ? (
                  <div style={{ fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.45 }}>
                    {holdsDollars ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span>It still holds SOV dollars its subtasks should.</span>
                        <button type="button" onClick={() => setLowWarn({ ids: [taskId], after: null })}
                          style={{ fontSize: 12.5, fontWeight: 600, padding: '2px 10px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
                            color: 'var(--warning)', background: 'var(--warning-soft)', border: '1px solid color-mix(in oklch, var(--warning) 40%, transparent)' }}>
                          Split to its subtasks
                        </button>
                      </div>
                    ) : 'Its SOV dollars sit on its subtasks: each one bills from its own %.'}
                  </div>
                ) : (
                <TaskSOVLinks
                  task={task}
                  projectId={projectId}
                  onAllocationsChanged={onAllocationsChanged}
                />
                )}

                {/* NOTES */}
                <SectionHeader title="Notes" />
                <textarea
                  name="notes" rows={3}
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Add notes for the field team or PM…"
                  style={{
                    ...inputBaseStyle,
                    height: 'auto', minHeight: 76, padding: '8px 10px',
                    resize: 'vertical', lineHeight: 1.45,
                  }}
                  onFocus={onFocusRing} onBlur={onBlurRing}
                />
                <div style={{ height: 8 }} />
              </SectionCards>
            )}
          </div>

          {/* ─────────── Error banner ─────────── */}
          {saveError && (
            <div style={{
              margin: '0 14px',
              padding: '10px 12px 10px 16px',
              background: 'var(--danger-soft)',
              borderRadius: '8px 8px 0 0',
              boxShadow: 'inset 3px 0 0 var(--danger)',
              border: '1px solid color-mix(in oklch, var(--danger) 18%, transparent)',
              borderBottom: 'none',
              display: 'flex', alignItems: 'flex-start', gap: 8,
              position: 'relative', top: -1, marginBottom: -1,
              color: 'var(--danger)',
            }}>
              <AlertCircleIcon />
              <div style={{ flex: 1, fontSize: 14, lineHeight: 1.45 }}>
                <span style={{ fontWeight: 600 }}>Couldn't save changes.</span>
                <span style={{ display: 'block', marginTop: 1, opacity: 0.85 }}>{saveError}</span>
              </div>
              <button
                type="button"
                onClick={() => { setSaveError(null); handleSave(); }}
                style={{
                  padding: '2px 8px', borderRadius: 5,
                  fontSize: 13, fontWeight: 500, color: 'var(--danger)',
                  border: '1px solid color-mix(in oklch, var(--danger) 30%, transparent)',
                  background: 'var(--bg-surface)',
                  cursor: 'pointer',
                }}
              >Retry</button>
            </div>
          )}

          {/* ─────────── Footer ─────────── */}
          {!loading && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '14px 20px',
              borderTop: '1px solid var(--border-subtle)',
              background: 'var(--bg-surface-2)',
              flexShrink: 0,
            }}>
              <button
                type="button"
                onClick={handleDelete}
                disabled={disable && !deleting}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '0 14px', height: 36, borderRadius: 8,
                  fontSize: 14.5, fontWeight: 500,
                  color: 'var(--danger)',
                  background: 'var(--bg-surface)',
                  border: '1px solid color-mix(in oklch, var(--danger) 35%, transparent)',
                  cursor: deleting ? 'wait' : (saving ? 'not-allowed' : 'pointer'),
                  opacity: saving && !deleting ? 0.45 : 1,
                }}
                onMouseEnter={(e) => { if (!disable || deleting) e.currentTarget.style.background = 'var(--danger-soft)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-surface)'; }}
              >
                {deleting ? <><Spinner size={12} color="var(--danger)" /> Deleting…</> : <><TrashIcon /> Delete</>}
              </button>
              <div style={{ flex: 1 }} />
              <button
                type="button"
                onClick={onClose}
                disabled={disable}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '0 14px', height: 36, borderRadius: 8,
                  fontSize: 14.5, fontWeight: 500,
                  color: 'var(--text-2)',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-default)',
                  cursor: disable ? 'not-allowed' : 'pointer',
                  opacity: disable ? 0.45 : 1,
                }}
                onMouseEnter={(e) => { if (!disable) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-surface)'; }}
              >Cancel</button>
              <button
                type="button"
                onClick={handleSave}
                disabled={disable}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '0 16px', height: 36, borderRadius: 8,
                  fontSize: 14.5, fontWeight: 500, color: '#fff',
                  background: 'var(--accent)',
                  border: '1px solid color-mix(in oklch, var(--accent) 50%, black)',
                  boxShadow: '0 1px 0 color-mix(in oklch, var(--accent) 35%, black), 0 1px 3px oklch(0.3 0.05 135 / 0.18)',
                  cursor: saving ? 'wait' : (deleting ? 'not-allowed' : 'pointer'),
                  opacity: deleting ? 0.45 : 1,
                }}
                onMouseEnter={(e) => { if (!disable) e.currentTarget.style.background = 'var(--accent-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--accent)'; }}
              >
                {saving ? <><Spinner /> Saving…</> : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>
      <ConfirmDialog {...confirmDialogProps} />
    </>
  );
};

export default TaskEditModal;
